/**
 * Iter-228x-bis — Compact cross-month search panel. Renders a togglable
 * accordion with an ExpenseSearchBar + flat result table + CSV export.
 *
 * Drop-in usage on any expense page:
 *   <CrossMonthSearchPanel kind="mine" categories={["Carburant","Hôtel",…]} />
 */
import React, { useEffect, useMemo, useState } from "react";
import { Search, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import ExpenseSearchBar, {
  computePresetRange, searchRowsToCsv, downloadCsv,
} from "./ExpenseSearchBar";

const fmt = (n) => Number(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 });

const STATUS_BADGE = {
  DRAFT: { label: "Brouillon", cls: "bg-zinc-100 text-zinc-700" },
  SUBMITTED: { label: "Soumise", cls: "bg-amber-100 text-amber-900" },
  APPROVED: { label: "Validée", cls: "bg-emerald-100 text-emerald-900" },
  PAID: { label: "Payée", cls: "bg-blue-100 text-blue-900" },
  REJECTED: { label: "Rejetée", cls: "bg-red-100 text-red-900" },
};

export default function CrossMonthSearchPanel({
  kind,                  // 'mine' | 'validated' | 'cabinet' | 'user'
  target_user_id = null, // required for kind=user
  categories = [],
  testidPrefix = "xmonth-search",
  initialOpen = false,
  csvFilenamePrefix = "recherche_depenses",
  renderActions = null,  // Iter-317 — optional per-row actions (edit, delete, receipt…)
  reloadKey = 0,         // bump to refetch after an edit made elsewhere on the page
  bulkActions = null,    // Iter-318 — (selectedRows, clear) => buttons; enables row checkboxes
  isRowSelectable = null,
}) {
  const [open, setOpen] = useState(initialOpen);
  const init = useMemo(() => {
    const range = computePresetRange("ytd");
    return { preset: "ytd", category: [], vendor: "", ...range };
  }, []);
  const [value, setValue] = useState(init);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const params = new URLSearchParams({ kind });
    if (target_user_id) params.append("target_user_id", target_user_id);
    if (value.from) params.append("date_from", value.from);
    if (value.to) params.append("date_to", value.to);
    if (value.vendor) params.append("vendor", value.vendor);
    (value.category || []).forEach((c) => params.append("category", c));
    setLoading(true);
    api.get(`/expenses/search?${params.toString()}`)
      .then((r) => setRows(r.data?.lines || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Échec recherche"))
      .finally(() => setLoading(false));
  }, [open, value, kind, target_user_id, reloadKey]);

  const exportCsv = () => {
    downloadCsv(`${csvFilenamePrefix}_${kind}_${(new Date()).toISOString().slice(0,10)}.csv`, searchRowsToCsv(rows, kind));
  };

  const total = rows.reduce((acc, l) => acc + (Number(l.amount_mad) || 0), 0);
  const rowId = (l) => l.expense_line_id || l.cabinet_line_id;
  const [selected, setSelected] = useState({});
  useEffect(() => { setSelected({}); }, [rows]);
  const selectable = rows.filter((r) => !isRowSelectable || isRowSelectable(r));
  const selectedRows = rows.filter((r) => selected[rowId(r)]);
  const allSelected = selectable.length > 0 && selectable.every((r) => selected[rowId(r)]);
  // Columns before "Montant" (for the total row): checkbox, date, source, user, vendor.
  const colsBeforeAmount = (bulkActions ? 1 : 0) + 1 + (kind === "all" ? 1 : 0) + (kind !== "mine" ? 1 : 0) + 1;

  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm overflow-hidden" data-testid={testidPrefix}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testidPrefix}-toggle`}
        className="w-full flex items-center gap-2 px-4 py-2 hover:bg-zinc-50 text-left"
      >
        <Search size={14} className="text-zinc-500" />
        <span className="text-sm font-medium text-zinc-900">Recherche multi-mois</span>
        <span className="text-xs text-zinc-500">— par catégorie, fournisseur, période</span>
        <div className="grow" />
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="p-3 border-t border-zinc-100 space-y-3">
          <ExpenseSearchBar
            testidPrefix={`${testidPrefix}-bar`}
            categories={categories}
            initialPreset="ytd"
            value={value}
            onChange={setValue}
            count={rows.length}
            onExport={rows.length ? exportCsv : null}
          />
          {loading && <div className="text-xs text-zinc-500 italic">Recherche en cours…</div>}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-zinc-500 italic text-center py-8" data-testid={`${testidPrefix}-empty`}>
              Aucun résultat — élargissez les critères ou la période.
            </div>
          )}
          {!loading && rows.length > 0 && bulkActions && selectedRows.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-md border border-sky-200 bg-sky-50 text-xs" data-testid={`${testidPrefix}-bulk-bar`}>
              <span className="font-medium text-sky-900" data-testid={`${testidPrefix}-bulk-count`}>{selectedRows.length} ligne(s) sélectionnée(s)</span>
              <div className="grow" />
              {bulkActions(selectedRows, () => setSelected({}))}
            </div>
          )}
          {!loading && rows.length > 0 && (
            <div className="border border-zinc-200 rounded">
              <table className="w-full text-xs table-fixed" data-testid={`${testidPrefix}-table`}>
                <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                  <tr>
                    {bulkActions && (
                      <th className="pl-3 pr-1 py-2 w-7">
                        <input type="checkbox" checked={allSelected} disabled={selectable.length === 0}
                          onChange={() => setSelected(allSelected ? {} : Object.fromEntries(selectable.map((r) => [rowId(r), true])))}
                          title="Tout sélectionner (lignes modifiables)" data-testid={`${testidPrefix}-select-all`} />
                      </th>
                    )}
                    <th className="px-2 py-2 text-left w-[5.5rem] hidden sm:table-cell">Date</th>
                    {kind === "all" && <th className="px-2 py-2 text-left w-16">Source</th>}
                    {kind !== "mine" && <th className="px-2 py-2 text-left w-28 hidden md:table-cell">Utilisateur</th>}
                    <th className="px-2 py-2 text-left">Fournisseur · catégorie</th>
                    <th className="px-2 py-2 text-right w-24 sm:w-28">Montant</th>
                    {kind !== "cabinet" && <th className="px-2 py-2 text-left w-20 hidden sm:table-cell">Statut</th>}
                    {renderActions && <th className="px-2 py-2 text-right w-24 sm:w-36">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((l) => {
                    const id = rowId(l);
                    const status = l.note_status ? STATUS_BADGE[l.note_status] : null;
                    const isCabinetRow = l.source === "cabinet" || (kind === "cabinet");
                    const canSelect = !isRowSelectable || isRowSelectable(l);
                    return (
                      <tr key={id} className={`border-t border-zinc-100 hover:bg-zinc-50 align-top ${selected[id] ? "bg-sky-50/60" : ""}`}>
                        {bulkActions && (
                          <td className="pl-3 pr-1 py-2">
                            <input type="checkbox" checked={!!selected[id]} disabled={!canSelect}
                              onChange={() => setSelected((p) => ({ ...p, [id]: !p[id] }))}
                              title={canSelect ? "Sélectionner" : "Ligne non modifiable (NDF soumise, validée ou payée)"}
                              data-testid={`${testidPrefix}-select-${id}`} />
                          </td>
                        )}
                        <td className="px-2 py-2 font-mono whitespace-nowrap hidden sm:table-cell">{l.invoice_date}</td>
                        {kind === "all" && (
                          <td className="px-2 py-2">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono ${isCabinetRow ? "bg-purple-100 text-purple-900" : "bg-sky-100 text-sky-900"}`}>
                              {l.source_label || (isCabinetRow ? "Cabinet" : "NDF")}
                            </span>
                          </td>
                        )}
                        {kind !== "mine" && <td className="px-2 py-2 truncate hidden md:table-cell" title={l.user_name}>{l.user_name || "—"}</td>}
                        <td className="px-2 py-2 min-w-0">
                          <div className="font-medium text-zinc-900 truncate" title={l.vendor}>{l.vendor || "—"}</div>
                          <div className="text-[10px] text-zinc-500 truncate">
                            <span className="sm:hidden font-mono">{l.invoice_date} · </span>{l.category || "—"}{l.invoice_number ? ` · N° ${l.invoice_number}` : ""}
                            {kind !== "cabinet" && status && <span className={`sm:hidden ml-1 px-1 rounded text-[9px] uppercase font-mono ${status.cls}`}>{status.label}</span>}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-right font-mono whitespace-nowrap">
                          <div className="font-semibold">{fmt(l.amount_mad)} MAD</div>
                          {(l.currency || "MAD") !== "MAD" && <div className="text-[10px] text-zinc-500">{fmt(l.amount)} {l.currency}</div>}
                        </td>
                        {kind !== "cabinet" && (
                          <td className="px-2 py-2 hidden sm:table-cell">
                            {isCabinetRow ? (
                              <span className="text-zinc-400 italic text-[10px]">N/A</span>
                            ) : status ? (
                              <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono ${status.cls}`}>{status.label}</span>
                            ) : "—"}
                          </td>
                        )}
                        {renderActions && (
                          <td className="px-2 py-1.5 text-right" data-testid={`${testidPrefix}-actions-${id}`}>
                            <div className="flex flex-wrap justify-end">{renderActions(l)}</div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-zinc-50 border-t border-zinc-200">
                  <tr>
                    <td colSpan={colsBeforeAmount} className="px-2 py-2 text-right text-[10px] uppercase font-mono text-zinc-500 hidden sm:table-cell">
                      Total MAD :
                    </td>
                    <td colSpan={colsBeforeAmount - 1} className="px-2 py-2 text-right text-[10px] uppercase font-mono text-zinc-500 sm:hidden">
                      Total :
                    </td>
                    <td className="px-2 py-2 text-right font-mono font-bold whitespace-nowrap">{fmt(total)} MAD</td>
                    {kind !== "cabinet" && <td className="hidden sm:table-cell" />}
                    {renderActions && <td />}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
